import { Navigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { getDefaultPageForRole, type AppRole } from "@/lib/permissions";

interface RoleGuardProps {
  children: React.ReactNode;
  roles: AppRole[];
}

/**
 * Per-route role guard. Renders children if the user's role is in the allowed list.
 * Redirects to their default page otherwise.
 * NOTE: This assumes the user is already authenticated (wrapped by ProtectedRoute).
 */
export function RoleGuard({ children, roles }: RoleGuardProps) {
  const { data: userRole, isLoading } = useUserRole();

  // Still loading — don't flash wrong content
  if (isLoading) return null;

  // No role assigned or role not in allowed list
  if (!userRole || !roles.includes(userRole)) {
    const fallback = userRole ? getDefaultPageForRole(userRole) : '/profile';
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
